import { getRowsPerPage } from '@/ui/layout/rows-per-page';
import { FilterCategory, FilterKey } from 'Types/filter/filterType';
import {
  filtersMap,
  generateFilterOptions,
  liveFiltersMap,
} from 'Types/filter/newFilter';
import { makeAutoObservable, reaction } from 'mobx';

import { sessionStore } from 'App/mstore';
import { checkFilterValue } from 'App/mstore/types/filter';
import FilterItem, { IFilter } from 'App/mstore/types/filterItem';
import Search from 'App/mstore/types/search';
import { searchService } from 'App/services';

export const checkValues = (key: any, value: any) => {
  if (key === FilterKey.DURATION) {
    return value[0] === '' || value[0] === null ? [0, value[1]] : value;
  }
  return value.filter((i: any) => i !== '' && i !== null);
};

export const filterMap = ({
  category,
  value,
  key,
  operator,
  sourceOperator,
  source,
  custom,
  isEvent,
  filters,
  sort,
  order,
}: any) => ({
  value: checkValues(key, value),
  custom,
  type: category === FilterCategory.METADATA ? FilterKey.METADATA : key,
  operator,
  source: category === FilterCategory.METADATA ? key.replace(/^_/, '') : source,
  sourceOperator,
  isEvent,
  filters: filters ? filters.map(filterMap) : [],
});

class SearchStoreLive {
  list = [];
  latestRequestTime: number | null = null;
  latestList = [];
  alertMetricId: number | null = null;
  instance = new Search({ sort: 'timestamp', order: 'desc' });
  instanceLive = new Search();
  savedSearch = new Search();
  filterSearchList: any = {};
  currentPage = 1;
  activeTab = { name: 'All', type: 'all' };
  scrollY = 0;
  sessions = [];
  total: number = 0;
  loadingFilterSearch = false;
  loading = false;
  /** the Fit list the page size comes from: the CoBrowse page, not the multiview picker */
  rowsList: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);

    // Reset currentPage to 1 only on filter changes
    reaction(
      () => this.instance,
      () => {
        this.currentPage = 1;
        // a new question supersedes whatever is in flight
        void this.fetchSessions(true);
      },
    );

    // Fetch sessions when currentPage changes
    reaction(
      () => this.currentPage,
      () => {
        void this.fetchSessions(true);
      },
    );
  }

  get filterList() {
    return generateFilterOptions(filtersMap);
  }

  get filterListLive() {
    return generateFilterOptions(liveFiltersMap);
  }

  fetchFilterSearch = async (params: any): Promise<void> => {
    this.loadingFilterSearch = true;

    try {
      const response: any[] = await searchService.fetchFilterSearch(params);

      this.filterSearchList = response.reduce(
        (
          acc: Record<string, { projectId: number; value: string }[]>,
          item: any,
        ) => {
          const { projectId, type, value } = item;
          if (!acc[type]) acc[type] = [];
          acc[type].push({ projectId, value });
          return acc;
        },
        {},
      );
    } catch (error) {
      console.error('Error fetching filter search:', error);
    } finally {
      this.loadingFilterSearch = false;
    }
  };

  edit(instance: Partial<Search>) {
    this.instance = new Search({ ...this.instance, ...instance });
  }

  apply(filter: any, fromUrl: boolean) {
    if (fromUrl) {
      this.instance = new Search(filter);
    } else {
      this.instance = { ...this.instance, ...filter };
    }
  }

  applyFilter(filter: any, force = false) {
    this.apply(filter, false);
  }

  updateCurrentPage(page: number) {
    this.currentPage = page;
  }

  clearSearch() {
    const { instance } = this;
    this.edit(
      new Search({
        rangeValue: instance.rangeValue,
        startDate: instance.startDate,
        endDate: instance.endDate,
        filters: [],
      }),
    );
  }

  addFilter(filter: any) {
    const index = this.instance.filters.findIndex(
      (i: FilterItem) => i.name === filter.name,
    );

    filter.value = checkFilterValue(filter.value);
    filter.filters = filter.filters
      ? filter.filters.map((subFilter: any) => ({
          ...subFilter,
          value: checkFilterValue(subFilter.value),
        }))
      : null;

    if (index > -1) {
      // Update existing filter
      // @ts-ignore
      this.instance.filters[index] = {
        ...this.instance.filters[index],
        value: this.instance.filters[index].value.concat(filter.value),
      };
    } else {
      // Add new filter (create a new array reference to notify MobX)
      this.instance.filters = [...this.instance.filters, filter];
    }

    this.instance = new Search({
      ...this.instance.toData(),
    });
  }

  addFilterByKeyAndValue(
    key: any,
    value: any,
    operator?: string,
    sourceOperator?: string,
    source?: string,
  ) {
    const defaultFilter = { ...filtersMap[key] };
    defaultFilter.value = value;

    if (operator) {
      defaultFilter.operator = operator;
    }
    if (defaultFilter.hasSource && source && sourceOperator) {
      defaultFilter.sourceOperator = sourceOperator;
      defaultFilter.source = source;
    }

    this.addFilter(defaultFilter);
  }

  updateFilter = (index: number, search: Partial<IFilter>) => {
    const newFilters = this.instance.filters.map((_filter: any, i: any) => {
      if (i === index) {
        search.value = checkFilterValue(search.value);
        return search;
      }
      return _filter;
    });

    this.instance = new Search({
      ...this.instance.toData(),
      filters: newFilters,
    });
  };

  removeFilter = (index: number) => {
    const newFilters = this.instance.filters.filter(
      (_filter: any, i: any) => i !== index,
    );

    this.instance = new Search({
      ...this.instance.toData(),
      filters: newFilters,
    });
  };

  setLoading = (val: boolean) => {
    this.loading = val;
  };

  setRowsList = (list?: string) => {
    this.rowsList = list;
  };

  private fetchSeq = 0;

  /** Unforced calls (the poll) skip while a request is in flight; forced ones
      supersede it, and only the latest response is applied. */
  fetchSessions = async (force?: boolean) => {
    if (!force && this.loading) {
      return;
    }
    const seq = ++this.fetchSeq;
    this.setLoading(true);
    try {
      await sessionStore.fetchLiveSessions(
        {
          ...this.instance.toSearch(),
          page: this.currentPage,
          limit: getRowsPerPage(this.rowsList),
        },
        () => seq !== this.fetchSeq,
      );
    } catch (e) {
      console.error('Error fetching sessions:', e);
    } finally {
      if (seq === this.fetchSeq) this.setLoading(false);
    }
  };
}

export default SearchStoreLive;
